import {
  all, takeLatest, select, put,
} from 'redux-saga/effects';
import deepmerge from 'deepmerge';
import { matchesType } from '../helper';
import {
  shop as actions,
  businessUnit as businessUnitActions,
  analytics as analyticsActions,
} from '../../../actions';
import { fetchOrder, createAndSignOrder } from '../../../api/orderApi';
import { getBusinessUnitDetails } from '../../../api/businessUnitApi';
import { Order } from '../../../models';
import { MAP_REQUIRED_BILLING_PROPS_TO_TEXT } from '../../../utils/constants';
import { getMissingBillingPropsToText } from '../../../utils/helpers';

function getStoreDetails(): void {
  try {
    window.parent.postMessage('getStoreDetails', process.env.REACT_APP_ShopifyAppUrl || '*');
  } catch (e) {
    console.log(`Failed to get store details. Reason: ${e.message}`);
  }
}

function* getOrder(): any {
  try {
    const shop = (yield select((state) => state.shop)) || {};
    const order = yield fetchOrder(shop.details.businessUnitId);
    yield put(actions.receiveOrder(order));
  } catch (e) {
    console.log(`Failed to get order. Reason: ${e.message}`);
  }
}

function* mapOrder(): any {
  const shop = (yield select((state) => state.shop)) || {};
  const { pricing } = yield select((state) => state.pricing);
  const { businessName, order, details } = shop;
  if (order) {
    delete order.price; // important in order not to duplicate pricing object 
    delete order.orders; 
  }
  const {
    address,
    address2,
    city,
    contactEmail,
    contactFirstName,
    contactLastName,
    contactPhoneNumber,
    country,
    countryCode,
    state,
    zipCode,
  } = details;

  const termsAndConditionsLink = process.env.REACT_APP_LegalUrl || '';
  const mapped = deepmerge<Order>(order, {
    businessUnitId: shop.details.businessUnitId,
    subscriberDetails: {
      companyName: businessName,
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhoneNumber,
      country,
      countryCode,
      address,
      address2,
      city,
      state,
      zipCode,
    },
    billingDetails: {
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhoneNumber,
      country,
      countryCode,
      address,
      address2,
      city,
      state,
      zipCode,
    },
    contractDetails: {
      termsAndConditionsLink,
    },
    billingCycle: pricing.billingCycle,
    price: {
      businessModules: pricing.businessModules,
      monthlyPrice: pricing.rawMonthlyPrice,
      contractPrice: pricing.rawContractPrice,
      billingCyclePrice: pricing.rawBillingCyclePrice,
      billingCycle: pricing.billingCycle,
      ...pricing.rawPrice,
    },
  });

  try {
    if (mapped.subscriberDetails.country.toLowerCase() !== 'united states' && mapped.subscriberDetails.country.toLowerCase() !== 'canada') {
      mapped.subscriberDetails.state = '';
      mapped.billingDetails.state = '';
    }
  } catch {
    console.error(`Problem reading country for ${businessName}`);
  }
  return mapped;
}

function* createOrder(): any {
  let orderCreated;

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const businessModule = searchParams.get('businessModule') || '';
    const { id } = yield createAndSignOrder(yield mapOrder());
    const shopDetails = (yield select((state) => state.shop.details)) || {};
    window.parent.postMessage(JSON.stringify({
      actionName: 'start_upgrade',
      orderId: id,
      name: 'Trustpilot Reviews',
      plan: businessModule,
      businessUnitId: shopDetails.businessUnitId,
    }), process.env.REACT_APP_ShopifyAppUrl || '*');
    orderCreated = true;
  } catch (e) {
    yield put(actions.createOrderFailure());
    yield put(analyticsActions.track('Error Occurred', {
      location: window.location.pathname,
      action: 'order creation failed',
      name: 'order-create',
    }));
    orderCreated = false;
  } finally {
    yield put(analyticsActions.track('Integration Button Clicked', {
      location: window.location.pathname,
      placement: 'Bottom right corner',
      action: 'next button clicked',
      name: 'next-button',
      orderCreated,
    }));
  }
}

function* receiveStoreDetails(): any {
  try {
    const shopDetails = (yield select((state) => state.shop.details)) || {};

    const isMissingDetails = Object.entries(MAP_REQUIRED_BILLING_PROPS_TO_TEXT).some(([key]) => !shopDetails[key]);
    const buDetails = yield getBusinessUnitDetails(shopDetails.businessUnitId);

    const businessUnitDetails = {
      companyName: buDetails.companyName || shopDetails.companyName,
      businessUnitName: buDetails.name.identifying,
    };

    yield put(businessUnitActions.setBusinessUnitDetails(businessUnitDetails));
    yield put(actions.setStoreDetails(shopDetails, isMissingDetails));

    yield put(analyticsActions.pageView('BillingApp page loaded', {
      location: window.location.pathname,
      missingBillingProps: getMissingBillingPropsToText(shopDetails),
      isMissingDetails,
    }));
  } catch (e) {
    yield put(analyticsActions.track('Error Occurred', {
      location: window.location.pathname,
      action: 'receive store details failed',
      name: 'receive-store-details',
    }));
  }
}

function* shop(): any {
  yield all([takeLatest(matchesType(actions.getStoreDetails), getStoreDetails)]);
  yield all([takeLatest(matchesType(actions.receiveStoreDetails), receiveStoreDetails)]);
  yield all([takeLatest(matchesType(actions.getOrder), getOrder)]);
  yield all([takeLatest(matchesType(actions.createOrder), createOrder)]);
}

export { shop };
